/**
 * useUserAvatar — the account's identity face, for the avatar/switcher control.
 *
 * Precedence: the user's CHOSEN profile avatar + background colour
 * (`consumer_profiles.profile_avatar_generation_id` / `_background_colour`),
 * falling back to their most recent generated avatar so the control still has a
 * face before they've picked one.
 *
 * Read-only and best-effort by design: the avatar is decoration on a menu
 * trigger, so any failure (no generations yet, RLS denial, network error)
 * resolves to `url: null` and the caller falls back to a person icon. It never
 * blocks or errors the surface it sits on.
 *
 * RLS scopes `avatar_generations` and `consumer_profiles` to the owner, so this
 * can only ever return the caller's own image.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { listGenerationsForUser } from "@/services/avatar";
import { getProfileDisplay } from "@/services/profile-display";

/**
 * ONE canonical in-memory profile-display state, shared by every consumer of
 * this hook (the account section preview AND the account/switcher button).
 *
 * Why a pushed value and not just a "refetch" ping: a bare version bump makes
 * every mounted control re-READ, which is async — so the button repainted a beat
 * late (or, on a read-after-write lag, with the OLD colour) while the account
 * section, which renders live form state, was already correct. Saving now PUSHES
 * the saved pair into this store, so all surfaces flip in the same commit; the
 * refetch still runs behind it as confirmation.
 */
let displayVersion = 0;
let current: { url: string | null; backgroundColour: string | null } | null = null;
const listeners = new Set<() => void>();

export function notifyProfileDisplayChanged(next?: {
  avatarUrl: string | null;
  backgroundColour: string | null;
}) {
  displayVersion += 1;
  // A colour-only change is a change: keep whichever fields were supplied.
  if (next) current = { url: next.avatarUrl, backgroundColour: next.backgroundColour };
  listeners.forEach((fn) => fn());
}

export function useUserAvatar(): { url: string | null; backgroundColour: string | null; loading: boolean } {
  const { user } = useAuth();
  const [url, setUrl] = useState<string | null>(current?.url ?? null);
  const [backgroundColour, setBackgroundColour] = useState<string | null>(current?.backgroundColour ?? null);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(displayVersion);

  useEffect(() => {
    const fn = () => {
      // Apply the pushed value immediately, then let the effect re-read.
      if (current) {
        setUrl(current.url);
        setBackgroundColour(current.backgroundColour);
      }
      setVersion(displayVersion);
    };
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);


  useEffect(() => {
    let cancelled = false;
    if (!user) {
      current = null;
      setUrl(null);
      setBackgroundColour(null);
      return;
    }
    setLoading(true);
    // Publish the resolved pair to the shared store so a control mounting later
    // starts from the same values instead of flashing a stale/blank face.
    const publish = (next: { url: string | null; backgroundColour: string | null }) => {
      current = next;
      if (cancelled) return;
      setUrl(next.url);
      setBackgroundColour(next.backgroundColour);
    };
    (async () => {
      try {
        const chosen = await getProfileDisplay(user.id);
        if (chosen.avatarUrl) {
          publish({ url: chosen.avatarUrl, backgroundColour: chosen.backgroundColour });
          return;
        }
        const { data } = await listGenerationsForUser(user.id);
        const rows = (data ?? []) as { generated_image_url: string | null }[];
        // listGenerationsForUser returns oldest-first, so the newest completed
        // avatar with an image is the last usable row.
        const latest = [...rows].reverse().find((r) => !!r.generated_image_url);
        publish({ url: latest?.generated_image_url ?? null, backgroundColour: chosen.backgroundColour });
      } catch {
        if (!cancelled) setUrl(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };

  }, [user, version]);

  return { url, backgroundColour, loading };
}
